import { Button, ButtonPrimary } from '@/components/button'
import { MainContent } from '@/components/content'
import { ArrowBack, ExpandMore } from '@/components/icons'
import { Delete } from '@/components/icons/Delete'
import { AutocompleteAddress, AutocompleteLocationInput, DatePickerInput, TextInput } from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { ConfirmModal } from '@/components/modal'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { eventTracker } from '@/config/trackingFunctions'
import {
  APP_CUSTOM_ERROR,
  FIELD_REQUIRED,
  GAME_CANNOT_DRAW_EMPTY,
  GAME_SCORE_REQUIRED,
  TEAM2_REQUIRED,
} from '@/constants/MessageConstants'
import { ROUTE_CLUBS, ROUTE_DASHBOARD } from '@/constants/Routes'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { useUserDetails } from '@/hooks/useUserDetails'
import { getClubDetail } from '@/pages/club/clubSlice'
import { clubSelector } from '@/pages/club/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { useAddMatch, useClubAddMatch, useClubEditMatch, useGetClubMatchDetail } from '@/services'
import { EventFormat } from '@/types'
import { MatchType } from '@/types/Match'
import AddMatchValidationSchema from '@/utils/schema/AddMatchValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import { ChangeEvent, useEffect, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Player } from './Player'
import { useHasDuprPlus, useHasFeature } from '@/hooks/useHasFeature'
import { ProviderRevenueCat } from '@/pages/external/ExternalPremiumLogin/RevenueCatProvider'
import { RevenueCatPurchase } from '@/pages/external/RevenuecatCheckout'
import { Feature } from '@/config/features'

type Props = {
  isClubMatch?: boolean
}

type NewMatch = {
  event: string
  location: AutocompleteAddress
  eventDate: string
  matchType: MatchType
  format: EventFormat
  team1: {
    game1: string | number
    game2: string | number
    game3: string | number
    game4: string | number
    game5: string | number
    player1: string | number
    player2: string | number
    winner: boolean
  }
  team2: {
    game1: string | number
    game2: string | number
    game3: string | number
    game4: string | number
    game5: string | number
    player1: string | number
    player2: string | number
    winner: boolean
  }
}

type FormValues = {
  matches: NewMatch[]
}

export const NewMatchCommon = ({ isClubMatch }: Props) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { clubId, matchId } = useParams()
  const dispatch = useAppDispatch()

  const { isShowDropdown } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)
  const { club } = useAppSelector(clubSelector)

  const { data: matchData } = useGetClubMatchDetail(clubId, matchId)

  const userData = useUserDetails()
  const [expands, setExpands] = useState<number[]>([0])
  const [isOpen, setIsOpen] = useState(false)
  const [checked, setChecked] = useState<number[]>([])
  const [exclude, setExclude] = useState<number[][]>([[]])
  const [isAddMatchLoading, setIsAddMatchLoading] = useState<boolean>(false)

  const today = new Date()

  useEffect(() => {
    if (!club && clubId) {
      dispatch(getClubDetail(clubId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultValues: FormValues = {
    matches: [
      {
        event: isClubMatch && !matchId ? club?.clubName || '' : '',
        location: {
          description: isClubMatch && !matchId ? club?.address.formattedAddress || '' : '',
          placeId: isClubMatch && !matchId ? club?.address.placeId || '' : '',
        },
        eventDate: moment(today).format('YYYY-MM-DD'),
        matchType: 'SIDE_ONLY',
        format: 'DOUBLES',
        team1: {
          game1: '',
          game2: '',
          game3: '',
          game4: '',
          game5: '',
          player1: isClubMatch ? '' : Number(user?.id),
          player2: '',
          winner: false,
        },
        team2: {
          game1: '',
          game2: '',
          game3: '',
          game4: '',
          game5: '',
          player1: '',
          player2: '',
          winner: false,
        },
      },
    ],
  }

  const { mutateAsync: addMatch } = useAddMatch()
  const { mutateAsync: clubAddMatch } = useClubAddMatch()
  const { mutateAsync: clubEditMatch } = useClubEditMatch()

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    register,
    setError,
    getValues,
    reset,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(AddMatchValidationSchema as any),
    mode: 'onTouched',
  })

  useEffect(() => {
    if (!matchId) {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId])

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'matches',
  })

  const openConfirmModal = (data: FormValues) => {
    const { matches } = data

    for (let i = 0; i < matches.length; i += 1) {
      if (
        matches?.[i]?.team1.player1 &&
        matches?.[i]?.team1.player2 &&
        matches?.[i]?.team2.player1 &&
        !matches?.[i]?.team2.player2
      )
        return setError(`matches.${i}.team2.player2`, { message: TEAM2_REQUIRED, type: 'required' })

      if (
        matches?.[i]?.team1.player1 &&
        !matches?.[i]?.team1.player2 &&
        matches?.[i]?.team2.player1 &&
        matches?.[i]?.team2.player2
      )
        return setError(`matches.${i}.team1.player2`, { message: TEAM2_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game2 && matches?.[i]?.team2.game2 === '')
        return setError(`matches.${i}.team2.game2`, { message: FIELD_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game2 === '' && matches?.[i]?.team2.game2)
        return setError(`matches.${i}.team1.game2`, { message: FIELD_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game3 && matches?.[i]?.team2.game3 === '')
        return setError(`matches.${i}.team2.game3`, { message: FIELD_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game3 === '' && matches?.[i]?.team2.game3)
        return setError(`matches.${i}.team1.game3`, { message: FIELD_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game4 && matches?.[i]?.team2.game4 === '')
        return setError(`matches.${i}.team2.game4`, { message: FIELD_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game4 === '' && matches?.[i]?.team2.game4)
        return setError(`matches.${i}.team1.game4`, { message: FIELD_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game5 && matches?.[i]?.team2.game5 === '')
        return setError(`matches.${i}.team2.game5`, { message: FIELD_REQUIRED, type: 'required' })

      if (matches?.[i]?.team1.game5 === '' && matches?.[i]?.team2.game5)
        return setError(`matches.${i}.team1.game5`, { message: FIELD_REQUIRED, type: 'required' })

      const teamWins = calculateTeamWins(matches?.[i], 5)
      const { team1Wins, team2Wins } = teamWins

      if (Number(matches?.[i]?.team1.game1) < 6 && Number(matches?.[i]?.team2.game1) < 6) {
        if (team1Wins > team2Wins) {
          return setError(`matches.${i}.team1.game1`, { message: GAME_SCORE_REQUIRED, type: 'min' })
        }
        return setError(`matches.${i}.team2.game1`, { message: GAME_SCORE_REQUIRED, type: 'min' })
      }

      if (
        matches?.[i]?.team1?.game2 &&
        matches?.[i]?.team2?.game2 &&
        Number(matches?.[i]?.team1.game2) < 6 &&
        Number(matches?.[i]?.team2.game2) < 6
      ) {
        if (team1Wins > team2Wins) {
          return setError(`matches.${i}.team1.game2`, { message: GAME_SCORE_REQUIRED, type: 'min' })
        }
        return setError(`matches.${i}.team2.game2`, { message: GAME_SCORE_REQUIRED, type: 'min' })
      }

      if (
        matches?.[i]?.team1?.game3 &&
        matches?.[i]?.team2?.game3 &&
        Number(matches?.[i]?.team1.game3) < 6 &&
        Number(matches?.[i]?.team2.game3) < 6
      ) {
        if (team1Wins > team2Wins) {
          return setError(`matches.${i}.team1.game3`, { message: GAME_SCORE_REQUIRED, type: 'min' })
        }
        return setError(`matches.${i}.team2.game3`, { message: GAME_SCORE_REQUIRED, type: 'min' })
      }

      if (
        matches?.[i]?.team1?.game4 &&
        matches?.[i]?.team2?.game4 &&
        Number(matches?.[i]?.team1.game4) < 6 &&
        Number(matches?.[i]?.team2.game4) < 6
      ) {
        if (team1Wins > team2Wins) {
          return setError(`matches.${i}.team1.game4`, { message: GAME_SCORE_REQUIRED, type: 'min' })
        }
        return setError(`matches.${i}.team2.game4`, { message: GAME_SCORE_REQUIRED, type: 'min' })
      }

      if (
        matches?.[i]?.team1?.game5 &&
        matches?.[i]?.team2?.game5 &&
        Number(matches?.[i]?.team1.game5) < 6 &&
        Number(matches?.[i]?.team2.game5) < 6
      ) {
        if (team1Wins > team2Wins) {
          return setError(`matches.${i}.team1.game5`, { message: GAME_SCORE_REQUIRED, type: 'min' })
        }
        return setError(`matches.${i}.team2.game5`, { message: GAME_SCORE_REQUIRED, type: 'min' })
      }

      if (matches?.[i]?.team1?.game1 === matches?.[i]?.team2?.game1) {
        setError(`matches.${i}.team1.game1`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        setError(`matches.${i}.team2.game1`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        return
      }
      if (
        matches?.[i]?.team1?.game2 &&
        matches?.[i]?.team2?.game2 &&
        matches?.[i]?.team1?.game2 === matches?.[i]?.team2?.game2
      ) {
        setError(`matches.${i}.team1.game2`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        setError(`matches.${i}.team2.game2`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        return
      }
      if (
        matches?.[i]?.team1?.game3 &&
        matches?.[i]?.team2?.game3 &&
        matches?.[i]?.team1?.game3 === matches?.[i]?.team2?.game3
      ) {
        setError(`matches.${i}.team1.game3`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        setError(`matches.${i}.team2.game3`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        return
      }
      if (
        matches?.[i]?.team1?.game4 &&
        matches?.[i]?.team2?.game4 &&
        matches?.[i]?.team1?.game4 === matches?.[i]?.team2?.game4
      ) {
        setError(`matches.${i}.team1.game4`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        setError(`matches.${i}.team2.game4`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        return
      }
      if (
        matches?.[i]?.team1?.game5 &&
        matches?.[i]?.team2?.game5 &&
        matches?.[i]?.team1?.game5 === matches?.[i]?.team2?.game5
      ) {
        setError(`matches.${i}.team1.game5`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        setError(`matches.${i}.team2.game5`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'validate' })
        return
      }
      if (team1Wins === team2Wins && isClubMatch) {
        return setError(`matches.${i}.team1.game1`, { message: GAME_CANNOT_DRAW_EMPTY, type: 'disabled' })
      }
    }
    setIsOpen(true)
  }

  const closeConfirmModal = () => setIsOpen(false)

  const toggleExpandMatch = (index: number) => {
    if (expands.includes(index)) {
      const filterExpands = expands.filter((item) => item !== index)
      setExpands(filterExpands)
    }
    if (!expands.includes(index)) setExpands([...expands, index])
  }

  const toggleYourselfCheck = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    if (!checked.includes(index) && e.target.checked && user) {
      setChecked([...checked, index])
      const filterExclude = [...exclude[index]].filter((item) => item !== getValues(`matches.${index}.team1.player1`))

      exclude[index] = [...filterExclude, user.id]

      setExclude(exclude)
      setValue(`matches.${index}.team1.player1`, user?.id, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  const onAddNewMatch = () => {
    const previousMatchVal = getValues(`matches.${fields.length - 1}`)
    const newMatchVal = [...defaultValues.matches]
    if (previousMatchVal && previousMatchVal.eventDate) {
      newMatchVal[0] = {
        ...newMatchVal[0],
        event: previousMatchVal?.event,
        eventDate: previousMatchVal?.eventDate,
        location: previousMatchVal?.location,
      }
    }
    append(newMatchVal)
    exclude.push([])
    setExclude(exclude)
    setExpands([...expands, fields.length])
  }

  const handleClick = (event: any) => {
    if (event.detail > 1) {
      return
    }

    const { value } = event.target
    if (value === '0') {
      const position = value.length
      event.target.setSelectionRange(position, position)
    }
  }

  const calculateTeamWins = (match: NewMatch, totalGames: number) => {
    let team1Wins = 0
    let team2Wins = 0
    for (let i = 1; i <= totalGames; i += 1) {
      const gameKeyTeam1 = `game${i}` as keyof typeof match.team1
      const gameKeyTeam2 = `game${i}` as keyof typeof match.team2
      const team1Score = Number(match.team1[gameKeyTeam1])
      const team2Score = Number(match.team2[gameKeyTeam2])
      if (team1Score > team2Score) team1Wins += 1

      if (team1Score < team2Score) team2Wins += 1
    }
    return { team1Wins, team2Wins }
  }

  const onGoBack = () => navigate(-1)

  const onSubmit = async (data: FormValues) => {
    const { matches } = data
    const errors: any[] = []

    for (let i = 0; i < matches.length; i += 1) {
      const teamWins = calculateTeamWins(matches?.[i], 5)
      const { team1Wins, team2Wins } = teamWins

      const payload: any = {
        clubId: clubId,
        ...(matchId
          ? {
              eventName: matches?.[i]?.event,
            }
          : {
              event: matches?.[i]?.event,
            }),
        eventDate: moment(matches?.[i]?.eventDate).format('YYYY-MM-DD'),
        location: matches?.[i]?.location?.description,
        matchType: matches?.[i]?.matchType,
        format: matches?.[i]?.team1?.player2 ? 'DOUBLES' : 'SINGLES',
        ...(!matchId && {
          team1: {
            game1: matches?.[i]?.team1?.game1 !== '' ? Number(matches?.[i]?.team1?.game1) : -1,
            game2: matches?.[i]?.team1?.game2 !== '' ? Number(matches?.[i]?.team1?.game2) : -1,
            game3: matches?.[i]?.team1?.game3 !== '' ? Number(matches?.[i]?.team1?.game3) : -1,
            game4: matches?.[i]?.team1?.game4 !== '' ? Number(matches?.[i]?.team1?.game4) : -1,
            game5: matches?.[i]?.team1?.game5 !== '' ? Number(matches?.[i]?.team1?.game5) : -1,
            player1: isClubMatch ? Number(matches?.[i]?.team1?.player1) : user?.id,
            player2: matches?.[i]?.team1?.player2 ? Number(matches?.[i]?.team1?.player2) : '',
            winner: team1Wins > team2Wins,
          },
          team2: {
            game1: matches?.[i]?.team2?.game1 !== '' ? Number(matches?.[i]?.team2?.game1) : -1,
            game2: matches?.[i]?.team2?.game2 !== '' ? Number(matches?.[i]?.team2?.game2) : -1,
            game3: matches?.[i]?.team2?.game3 !== '' ? Number(matches?.[i]?.team2?.game3) : -1,
            game4: matches?.[i]?.team2?.game4 !== '' ? Number(matches?.[i]?.team2?.game4) : -1,
            game5: matches?.[i]?.team2?.game5 !== '' ? Number(matches?.[i]?.team2?.game5) : -1,
            player1: Number(matches?.[i]?.team2?.player1),
            player2: matches?.[i]?.team2?.player2 ? Number(matches?.[i]?.team2?.player2) : '',
            winner: team2Wins > team1Wins,
          },
        }),
        ...(matchId && {
          teams: [
            {
              id: matchData?.result?.teams?.[0]?.id,
              game1: matches?.[i]?.team1?.game1 !== '' ? Number(matches?.[i]?.team1?.game1) : -1,
              game2: matches?.[i]?.team1?.game2 !== '' ? Number(matches?.[i]?.team1?.game2) : -1,
              game3: matches?.[i]?.team1?.game3 !== '' ? Number(matches?.[i]?.team1?.game3) : -1,
              game4: matches?.[i]?.team1?.game4 !== '' ? Number(matches?.[i]?.team1?.game4) : -1,
              game5: matches?.[i]?.team1?.game5 !== '' ? Number(matches?.[i]?.team1?.game5) : -1,
              player1: isClubMatch ? Number(matches?.[i]?.team1?.player1) : user?.id,
              player2: matches?.[i]?.team1?.player2 ? Number(matches?.[i]?.team1?.player2) : '',
              winner: team1Wins > team2Wins,
            },
            {
              id: matchData?.result?.teams?.[1]?.id,
              game1: matches?.[i]?.team2?.game1 !== '' ? Number(matches?.[i]?.team2?.game1) : -1,
              game2: matches?.[i]?.team2?.game2 !== '' ? Number(matches?.[i]?.team2?.game2) : -1,
              game3: matches?.[i]?.team2?.game3 !== '' ? Number(matches?.[i]?.team2?.game3) : -1,
              game4: matches?.[i]?.team2?.game4 !== '' ? Number(matches?.[i]?.team2?.game4) : -1,
              game5: matches?.[i]?.team2?.game5 !== '' ? Number(matches?.[i]?.team2?.game5) : -1,
              player1: Number(matches?.[i]?.team2?.player1),
              player2: matches?.[i]?.team2?.player2 ? Number(matches?.[i]?.team2?.player2) : '',
              winner: team2Wins > team1Wins,
            },
          ],
          matchId: matchId,
          matchSource: 'CLUB',
          reason: 'Match Edited by Director',
          requestedBy: user?.referralCode,
        }),
      }
      setIsAddMatchLoading(true)
      if (isClubMatch && !matchId)
        await clubAddMatch(payload)
          .then(() => {
            eventTracker(TRACKING_TYPES.ADD_CLUB_MATCH_SUCCESS, {
              ...userData,
              'Page Name': PAGE_NAMES.ADD_CLUB_MATCH,
              'Club ID': clubId,
            })
          })
          .catch((error) => errors.push(error))
      if (!isClubMatch && !matchId)
        await addMatch(payload)
          .then(() => {
            eventTracker(TRACKING_TYPES.MATCH_ADDED, {
              ...userData,
              'Page Name': PAGE_NAMES.ADD_MATCH,
            })
          })
          .catch((error) => errors.push(error))
      if (matchId) await clubEditMatch(payload).catch((error) => errors.push(error))
    }
    if (errors.length === 0) {
      closeConfirmModal()
      toast.success(`${matches.length > 1 ? 'Matches have been submitted' : 'Match has been submitted'}`)
      if (isClubMatch || matchId) navigate(`${ROUTE_CLUBS}/${clubId}`, { relative: 'path', replace: true })
      if (!isClubMatch) navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    } else {
      closeConfirmModal()
      if (errors[0].response?.data) {
        if (
          errors[0].response.data.errors &&
          typeof errors[0].response.data.errors === 'object' &&
          Object.keys(errors[0].response.data.errors).length
        ) {
          toast.error(Object.keys(errors[0].response.data.errors)[0])
        } else {
          toast.error(errors[0].response.data.message ? errors[0].response.data.message : APP_CUSTOM_ERROR)
        }
      } else {
        toast.error(APP_CUSTOM_ERROR)
      }
    }
    setIsAddMatchLoading(false)
  }

  const countKeys = (obj: any) => {
    let count = 0
    for (const key in obj) {
      const value = obj[key]
      if (typeof value === 'object' && value !== null) {
        if (value.type) {
          count++
        }
        count += countKeys(value)
      }
    }
    return count
  }

  useEffect(() => {
    if (!isClubMatch && user) {
      exclude.map((matchExclude) => matchExclude.push(user?.id))
      setExclude(exclude)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isClubMatch])

  const onPlayerChange = (index: number, team: 'team1' | 'team2', player: 'player1' | 'player2', id: number) => {
    exclude[index] = [...exclude[index], id]
    setExclude(exclude)
    setValue(`matches.${index}.${team}.${player}`, id, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const onPlayerRemove = (index: number, team: 'team1' | 'team2', player: 'player1' | 'player2') => {
    const filterExclude = [...exclude[index]].filter((item) => item !== watch(`matches.${index}.${team}.${player}`))
    exclude[index] = filterExclude
    setExclude(exclude)
    setValue(`matches.${index}.${team}.${player}`, '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    const filterChecked = checked.filter((item) => item !== index)
    setChecked(filterChecked)
  }

  useEffect(() => {
    if (matchData && matchId) {
      setValue(`matches.${0}.event`, matchData.result.eventName)
      setValue(`matches.${0}.location.description`, matchData.result.location)
      setValue(`matches.${0}.eventDate`, matchData.result.eventDate)
      setValue(`matches.${0}.matchType`, matchData.result.matchType)
      setValue(`matches.${0}.team1.player1`, matchData.result.teams?.[0]?.player1?.id, {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue(`matches.${0}.team1.player2`, matchData.result.teams?.[0]?.player2?.id)
      setValue(`matches.${0}.team1.winner`, matchData.result.teams?.[0]?.winner)
      setValue(
        `matches.${0}.team1.game1`,
        matchData.result.teams?.[0]?.game1 !== -1 ? matchData.result.teams?.[0]?.game1 : '',
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      )
      setValue(
        `matches.${0}.team1.game2`,
        matchData.result.teams?.[0]?.game2 !== -1 ? matchData.result.teams?.[0]?.game2 : ''
      )
      setValue(
        `matches.${0}.team1.game3`,
        matchData.result.teams?.[0]?.game3 !== -1 ? matchData.result.teams?.[0]?.game3 : ''
      )
      setValue(
        `matches.${0}.team1.game4`,
        matchData.result.teams?.[0]?.game4 !== -1 ? matchData.result.teams?.[0]?.game4 : ''
      )
      setValue(
        `matches.${0}.team1.game5`,
        matchData.result.teams?.[0]?.game5 !== -1 ? matchData.result.teams?.[0]?.game5 : ''
      )

      setValue(`matches.${0}.team2.player1`, matchData.result.teams?.[1]?.player1?.id, {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue(`matches.${0}.team2.player2`, matchData.result.teams?.[1]?.player2?.id)
      setValue(`matches.${0}.team2.winner`, matchData.result.teams?.[1]?.winner)
      setValue(
        `matches.${0}.team2.game1`,
        matchData.result.teams?.[1]?.game1 !== -1 ? matchData.result.teams?.[1]?.game1 : '',
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      )
      setValue(
        `matches.${0}.team2.game2`,
        matchData.result.teams?.[1]?.game2 !== -1 ? matchData.result.teams?.[1]?.game2 : ''
      )
      setValue(
        `matches.${0}.team2.game3`,
        matchData.result.teams?.[1]?.game3 !== -1 ? matchData.result.teams?.[1]?.game3 : ''
      )
      setValue(
        `matches.${0}.team2.game4`,
        matchData.result.teams?.[1]?.game4 !== -1 ? matchData.result.teams?.[1]?.game4 : ''
      )
      setValue(
        `matches.${0}.team2.game5`,
        matchData.result.teams?.[1]?.game5 !== -1 ? matchData.result.teams?.[1]?.game5 : ''
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchData, matchId])

  useEffect(() => {
    if (Object.keys(errors).length) {
      const totalCount = (errors?.matches as any)?.reduce((sum: number, current: any) => sum + countKeys(current), 0)
      if (totalCount && totalCount > 0)
        toast.error(
          `There are ${totalCount} Errors in ${getValues('matches').length} Matches. Please fix them and Submit again.`
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors])

  const handleMatchEventDateChange = (date: Date | null, index: number) => {
    if (date) {
      setValue(`matches.${index}.eventDate`, moment(date).format('YYYY-MM-DD'), {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  const isDuprPlus = useHasDuprPlus()
  const duprBasicCanAddMatch = useHasFeature(Feature.DuprBasicCanAddNonClubMatch)

  const canAddMatch = isClubMatch || isDuprPlus || duprBasicCanAddMatch
  return (
    <>
      {!isShowDropdown && <MobileSidebar />}

      <MainLayout>
        <MainSidebar />
        <MainContent>
          {!canAddMatch ? (
            <>
              <h1 className="text-white align-center text-center">This is a DUPR+ Only feature</h1>
              <ProviderRevenueCat>
                <RevenueCatPurchase />
              </ProviderRevenueCat>
            </>
          ) : (
            <div className="flex flex-col gap-y-4 md:gap-y-6 mb-20 lg:mb-0 pb-6 lg:pb-10">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  {isClubMatch && (
                    <Button onClick={onGoBack} className="hover:opacity-50">
                      <ArrowBack className="text-white" />
                    </Button>
                  )}
                  <h1 className={`text-2xl md:text-[32px] md:leading-10 font-bold text-white ${isClubMatch && 'ml-3'}`}>
                    {matchId ? t('common.editMatch') : t('common.addMatches')}
                  </h1>
                </div>
                <Button
                  className="bg-white bg-opacity-10 py-2 px-4 text-white font-semibold text-sm hidden sm:flex"
                  onClick={handleSubmit(openConfirmModal)}
                >
                  {matchId ? t('common.saveMatch') : t('common.submitMatches')}
                </Button>
                <Button
                  className="bg-white bg-opacity-10 py-2 px-4 text-white font-semibold text-sm flex sm:hidden"
                  onClick={handleSubmit(openConfirmModal)}
                >
                  {t('common.submit')}
                </Button>
                <ConfirmModal
                  open={isOpen}
                  onClose={closeConfirmModal}
                  disableSubmitBtn={isAddMatchLoading}
                  title={`${t('common.submit')} ${
                    fields.length > 1 ? `${fields.length} ${t('common.matches')}` : t('common.match')
                  }`}
                  desc={t('common.confirmSubmitMatches')}
                  onSubmit={handleSubmit(onSubmit)}
                />
              </div>

              {fields.map((field, index) => {
                const isYourself = checked.includes(index)
                const disableCheckbox =
                  isYourself ||
                  !!(
                    watch(`matches.${index}.team1.player1`) &&
                    watch(`matches.${index}.team1.player2`) &&
                    watch(`matches.${index}.team1.player1`) !== user?.id &&
                    watch(`matches.${index}.team1.player2`) !== user?.id
                  ) ||
                  watch(`matches.${index}.team1.player2`) === user?.id ||
                  watch(`matches.${index}.team2.player1`) === user?.id ||
                  watch(`matches.${index}.team2.player2`) === user?.id

                const team1ScoreError =
                  errors?.matches?.[index]?.team1?.game1?.type === 'min' ||
                  errors?.matches?.[index]?.team1?.game2?.type === 'min' ||
                  errors?.matches?.[index]?.team1?.game3?.type === 'min' ||
                  errors?.matches?.[index]?.team1?.game4?.type === 'min' ||
                  errors?.matches?.[index]?.team1?.game5?.type === 'min'

                const team2ScoreError =
                  errors?.matches?.[index]?.team2?.game1?.type === 'min' ||
                  errors?.matches?.[index]?.team2?.game2?.type === 'min' ||
                  errors?.matches?.[index]?.team2?.game3?.type === 'min' ||
                  errors?.matches?.[index]?.team2?.game4?.type === 'min' ||
                  errors?.matches?.[index]?.team2?.game5?.type === 'min'

                const isTieGame =
                  errors?.matches?.[index]?.team1?.game1?.type === 'validate' ||
                  errors?.matches?.[index]?.team1?.game2?.type === 'validate' ||
                  errors?.matches?.[index]?.team1?.game3?.type === 'validate' ||
                  errors?.matches?.[index]?.team1?.game4?.type === 'validate' ||
                  errors?.matches?.[index]?.team1?.game5?.type === 'validate'

                const isTieMatch = errors?.matches?.[index]?.team1?.game1?.type === 'disabled'

                const hasError = errors.matches?.[index]

                return (
                  <div className="px-6 pt-4 pb-4 rounded-2xl bg-white flex flex-col gap-y-4" key={field.id}>
                    <div className="flex flex-row pt-4 pb-2 justify-between items-center">
                      <div className="flex items-center gap-x-2">
                        <h3 className="text-navy-900 font-semibold text-xl">
                          {t('common.match')} {matchId ? 'Detail' : index + 1}
                        </h3>
                        {hasError && <span className="text-red-600 text-2xl font-semibold">*</span>}
                      </div>
                      <div className="flex gap-x-4 items-center">
                        {fields.length !== 1 && (
                          <button
                            onClick={() => {
                              remove(index)
                              exclude.splice(index, 1)
                              setExclude(exclude)
                            }}
                            className="p-1"
                          >
                            <Delete className="text-navy-900 hover:text-opacity-70" />
                          </button>
                        )}
                        <button onClick={() => toggleExpandMatch(index)} className="p-1">
                          <ExpandMore
                            className={`text-navy-900 hover:text-opacity-70 transition-all duration-300 ${
                              expands.includes(index) ? 'rotate-180' : 'rotate-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    <div
                      className={`flex flex-col gap-y-4 transition-all duration-500 ease-in-out ${
                        expands.includes(index)
                          ? 'max-h-[1440px] sm:max-h-[720px]'
                          : 'max-h-0 opacity-0 overflow-hidden'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:gap-x-4 gap-y-4 w-full">
                        <div className="flex flex-col md:w-[340px]">
                          <TextInput
                            placeholder={t('common.eventPlaceHolder')}
                            labelClassName="!text-navy-900"
                            label={t('common.event')}
                            {...register(`matches.${index}.event`)}
                          />
                        </div>
                        <div className="flex flex-col md:w-[340px]">
                          <Controller
                            name={`matches.${index}.location`}
                            control={control}
                            render={({ field }) => (
                              <AutocompleteLocationInput
                                {...field}
                                label={t('common.location')}
                                placeholder={t('common.location')}
                                labelClassName="!text-navy-900"
                                setValue={(location) =>
                                  setValue(`matches.${index}.location`, location, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col md:w-[204px]">
                        <DatePickerInput
                          label={t('common.date')}
                          labelClassName="!text-navy-900"
                          selected={watch(`matches.${index}.eventDate`, defaultValues?.matches?.[index]?.eventDate)}
                          maxDate={new Date()}
                          onChange={(date: Date | null) => handleMatchEventDateChange(date, index)}
                          error={errors.matches?.[index]?.eventDate?.message}
                        />
                      </div>

                      {isClubMatch && !matchId && (
                        <div className="flex items-center justify-center gap-x-2 py-2">
                          <input
                            type="checkbox"
                            className={`w-5 h-5 ${disableCheckbox && 'cursor-not-allowed'}`}
                            disabled={disableCheckbox}
                            checked={isYourself}
                            onChange={(e) => toggleYourselfCheck(e, index)}
                          />
                          <p
                            className={`text-sm ${
                              disableCheckbox ? 'text-slate-400' : 'text-navy-900 text-opacity-70'
                            } font-medium`}
                          >
                            {t('common.checkboxAddYourSelf')}
                          </p>
                        </div>
                      )}

                      <div className="flex flex-col md:flex-row justify-between md:gap-x-8 gap-y-4">
                        <div className="flex-1 flex flex-col gap-y-4">
                          <div className="flex flex-col gap-y-2">
                            <h2 className="text-sm font-semibold text-navy-900">{t('common.team1')}</h2>
                            {isClubMatch ? (
                              <Controller
                                name={`matches.${index}.location`}
                                control={control}
                                render={() => (
                                  <Player
                                    error={errors?.matches?.[index]?.team1?.player1?.message}
                                    isSingle={!watch(`matches.${index}.team1.player2`)}
                                    onChange={(id) => {
                                      onPlayerChange(index, 'team1', 'player1', id)
                                      if (id === user?.id) setChecked([...checked, index])
                                    }}
                                    isYourself={isYourself}
                                    onChangeRemove={() => onPlayerRemove(index, 'team1', 'player1')}
                                    isClubMatchNew
                                    exclude={exclude[index]}
                                    playerData={matchId ? matchData?.result?.teams?.[0]?.player1 : null}
                                  />
                                )}
                              />
                            ) : (
                              <Controller
                                name={`matches.${index}.location`}
                                control={control}
                                render={() => <Player isMe isSingle={!watch(`matches.${index}.team1.player2`)} />}
                              />
                            )}
                            <Controller
                              name={`matches.${index}.location`}
                              control={control}
                              render={() => (
                                <Player
                                  error={errors?.matches?.[index]?.team1?.player2?.message}
                                  isSingle={!watch(`matches.${index}.team1.player2`)}
                                  onChange={(id) => onPlayerChange(index, 'team1', 'player2', id)}
                                  onChangeRemove={() => onPlayerRemove(index, 'team1', 'player2')}
                                  isClubMatchNew={isClubMatch}
                                  exclude={exclude[index]}
                                  playerData={matchId ? matchData?.result?.teams?.[0]?.player2 : null}
                                />
                              )}
                            />
                          </div>

                          <div className="flex flex-col gap-y-2">
                            <h2 className="text-sm font-semibold text-navy-900">{t('common.team2')}</h2>
                            <Controller
                              name={`matches.${index}.location`}
                              control={control}
                              render={() => (
                                <Player
                                  error={errors?.matches?.[index]?.team2?.player1?.message}
                                  isSingle={!watch(`matches.${index}.team2.player2`)}
                                  onChange={(id) => onPlayerChange(index, 'team2', 'player1', id)}
                                  onChangeRemove={() => onPlayerRemove(index, 'team2', 'player1')}
                                  isClubMatchNew={isClubMatch}
                                  exclude={exclude[index]}
                                  playerData={matchId ? matchData?.result?.teams?.[1]?.player1 : null}
                                />
                              )}
                            />
                            <Controller
                              name={`matches.${index}.location`}
                              control={control}
                              render={() => (
                                <Player
                                  error={errors?.matches?.[index]?.team2?.player2?.message}
                                  isSingle={!watch(`matches.${index}.team2.player2`)}
                                  onChange={(id) => onPlayerChange(index, 'team2', 'player2', id)}
                                  onChangeRemove={() => onPlayerRemove(index, 'team2', 'player2')}
                                  isClubMatchNew={isClubMatch}
                                  exclude={exclude[index]}
                                  playerData={matchId ? matchData?.result?.teams?.[1]?.player2 : null}
                                />
                              )}
                            />
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-center gap-y-3 relative">
                          <h2 className="text-sm font-semibold text-navy-900 lg:absolute top-0 left-0">
                            {t('common.score')}
                          </h2>
                          {(isTieMatch || team1ScoreError || team2ScoreError || isTieGame) && (
                            <div className={`text-boldOrange -mt-1 font-medium text-xs`}>
                              {/* text error condition that scores less than 6 must be displayed first */}
                              {team1ScoreError || team2ScoreError ? GAME_SCORE_REQUIRED : GAME_CANNOT_DRAW_EMPTY}
                            </div>
                          )}
                          {/* Match Type */}
                          <div className="flex justify-center">
                            <div className={`border rounded-full ${matchId ? 'opacity-80' : ''}`}>
                              <Button
                                className={`${
                                  watch(`matches.${index}.matchType`) === 'SIDE_ONLY'
                                    ? 'bg-primary-500 text-white'
                                    : 'text-navy-900'
                                } p-2 text-xs font-medium w-24 ${matchId ? 'cursor-not-allowed' : ''}`}
                                onClick={() =>
                                  setValue(`matches.${index}.matchType`, 'SIDE_ONLY', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                                disabled={!!matchId}
                              >
                                {t('common.standard')}
                              </Button>
                              <Button
                                className={`${
                                  watch(`matches.${index}.matchType`) === 'RALLY'
                                    ? 'bg-primary-500 text-white'
                                    : 'text-navy-900'
                                } p-2 text-xs font-medium w-24 ${matchId ? 'cursor-not-allowed' : ''}`}
                                onClick={() =>
                                  setValue(`matches.${index}.matchType`, 'RALLY', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                                disabled={!!matchId}
                              >
                                {t('common.rally')}
                              </Button>
                            </div>
                          </div>
                          {/* Score */}
                          <div className="flex flex-col lg:flex-row lg:justify-center gap-y-4 lg:space-x-6 md:items-center">
                            {/* Game 1 */}
                            <div className={`flex items-center lg:flex-col justify-between md:justify-start gap-y-4`}>
                              <div className="md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team1.game1`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team1?.game1 &&
                                        errors?.matches?.[index]?.team1?.game1?.type !== 'disabled' &&
                                        'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team1.game1`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team1.game1`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team2.game1`,
                                            watch(`matches.${index}.team2.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                              <div className={`justify-center flex font-semibold text-sm text-navy-900 mx-5 lg:mx-0`}>
                                {t('common.game1')}
                              </div>
                              <div className="justify-end md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team2.game1`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      className={`bg-white rounded-2xl py-4 px-2 text-sm text-navy-900 w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team2?.game1 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team2.game1`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team2.game1`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            {/* Game 2 */}
                            <div
                              className={`${
                                watch(`matches.${index}.team1.game1`) && watch(`matches.${index}.team2.game1`)
                                  ? ''
                                  : 'opacity-50'
                              } flex items-center lg:flex-col justify-between md:justify-start gap-y-4`}
                            >
                              <div className="md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team1.game2`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game1`) || !watch(`matches.${index}.team2.game1`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team1?.game2 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team1.game2`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team1.game2`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team2.game2`,
                                            watch(`matches.${index}.team2.game2`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game1`,
                                            watch(`matches.${index}.team2.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                              <div className={`justify-center flex font-semibold text-sm text-navy-900 mx-5 lg:mx-0`}>
                                {t('common.game2')}
                              </div>
                              <div className="justify-end md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team2.game2`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game1`) || !watch(`matches.${index}.team2.game1`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team2?.game2 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team2.game2`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team2.game2`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team1.game2`,
                                            watch(`matches.${index}.team1.game2`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game1`,
                                            watch(`matches.${index}.team2.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            {/* Game 3 */}
                            <div
                              className={`${
                                watch(`matches.${index}.team1.game2`) && watch(`matches.${index}.team2.game2`)
                                  ? ''
                                  : 'opacity-50'
                              } flex items-center lg:flex-col justify-between md:justify-start gap-y-4`}
                            >
                              <div className="md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team1.game3`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game2`) || !watch(`matches.${index}.team2.game2`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team1?.game3 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team1.game3`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team1.game3`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team2.game3`,
                                            watch(`matches.${index}.team2.game3`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game2`,
                                            watch(`matches.${index}.team1.game2`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game2`,
                                            watch(`matches.${index}.team2.game2`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                              <div className={`justify-center flex font-semibold text-sm text-navy-900 mx-5 lg:mx-0`}>
                                {t('common.game3')}
                              </div>
                              <div className="justify-end md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team2.game3`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game2`) || !watch(`matches.${index}.team2.game2`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team2?.game3 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team2.game3`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team2.game3`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team1.game3`,
                                            watch(`matches.${index}.team1.game3`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game2`,
                                            watch(`matches.${index}.team1.game2`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game2`,
                                            watch(`matches.${index}.team2.game2`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            {/* Game 4 */}
                            <div
                              className={`${
                                watch(`matches.${index}.team1.game3`) && watch(`matches.${index}.team2.game3`)
                                  ? ''
                                  : 'opacity-50'
                              } flex items-center lg:flex-col justify-between md:justify-start gap-y-4`}
                            >
                              <div className="md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team1.game4`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game3`) || !watch(`matches.${index}.team2.game3`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team1?.game4 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team1.game4`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team1.game4`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team2.game4`,
                                            watch(`matches.${index}.team2.game4`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game3`,
                                            watch(`matches.${index}.team1.game3`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game3`,
                                            watch(`matches.${index}.team2.game3`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          // clear tie match
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                              <div className={`justify-center flex font-semibold text-sm text-navy-900 mx-5 lg:mx-0`}>
                                {t('common.game4')}
                              </div>
                              <div className="justify-end md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team2.game4`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game3`) || !watch(`matches.${index}.team2.game3`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team2?.game4 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team2.game4`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team2.game4`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team1.game4`,
                                            watch(`matches.${index}.team1.game4`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game3`,
                                            watch(`matches.${index}.team1.game3`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game3`,
                                            watch(`matches.${index}.team2.game3`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          // clear tie match
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            {/* Game 5 */}
                            <div
                              className={`${
                                watch(`matches.${index}.team1.game4`) && watch(`matches.${index}.team2.game4`)
                                  ? ''
                                  : 'opacity-50'
                              } flex items-center lg:flex-col justify-between md:justify-start gap-y-4`}
                            >
                              <div className="md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team1.game5`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game4`) || !watch(`matches.${index}.team2.game4`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team1?.game5 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team1.game5`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team1.game5`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team2.game5`,
                                            watch(`matches.${index}.team2.game5`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team1.game4`,
                                            watch(`matches.${index}.team1.game4`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game4`,
                                            watch(`matches.${index}.team2.game4`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          // clear tie match
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                              <div className={`justify-center flex font-semibold text-sm text-navy-900 mx-5 lg:mx-0`}>
                                {t('common.game5')}
                              </div>
                              <div className="justify-end md:justify-center flex">
                                <Controller
                                  name={`matches.${index}.team2.game5`}
                                  control={control}
                                  render={() => (
                                    <input
                                      onClick={(e) => handleClick(e)}
                                      disabled={
                                        !watch(`matches.${index}.team1.game4`) || !watch(`matches.${index}.team2.game4`)
                                      }
                                      className={`bg-white text-navy-900 rounded-2xl py-4 px-2 text-sm w-[72px] text-center focus:outline-none border ${
                                        errors?.matches?.[index]?.team2?.game5 && 'border-boldOrange'
                                      }`}
                                      value={watch(`matches.${index}.team2.game5`)}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const letterRegex = /^[0-9]*$/
                                        if (e.target.value.match(letterRegex)) {
                                          setValue(`matches.${index}.team2.game5`, e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          })
                                          setValue(
                                            `matches.${index}.team1.game5`,
                                            watch(`matches.${index}.team1.game5`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          // clear game match and vv
                                          setValue(
                                            `matches.${index}.team1.game4`,
                                            watch(`matches.${index}.team1.game4`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          setValue(
                                            `matches.${index}.team2.game4`,
                                            watch(`matches.${index}.team2.game4`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                          // clear tie match
                                          setValue(
                                            `matches.${index}.team1.game1`,
                                            watch(`matches.${index}.team1.game1`),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }
                                      }}
                                      maxLength={2}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
              {!matchId && (
                <ButtonPrimary title={t('common.addAnotherMatch')} className="w-full" onClick={onAddNewMatch} />
              )}
            </div>
          )}
        </MainContent>
      </MainLayout>
    </>
  )
}
